import { fetchBodiesBootstrap } from "@/lib/api/imap";
import {
  sanitizeMailMessageDetail,
  writeMessageBodyCache,
} from "@/lib/cache/messageBodyCache";
import { INBOX_BODIES_BOOTSTRAP_PAGE } from "@/lib/constants/inbox";
import { notifyBodiesWarmed } from "@/lib/mail/mailBodyWarmEvents";
import { isFetchedBodyComplete, isMessageBodyInMemory } from "@/lib/mail/messageBodyCacheStatus";

const inflight = new Map<string, Promise<number>>();

function folderKey(accountId: number, folder: string): string {
  return `${accountId}\0${folder}`;
}

function buildQueue(
  accountId: number,
  folder: string,
  uids: number[],
  priority?: ReadonlySet<number>,
  maxUids?: number
): number[] {
  const unique: number[] = [];
  const seen = new Set<number>();
  for (const uid of uids) {
    if (uid <= 0 || seen.has(uid)) continue;
    seen.add(uid);
    unique.push(uid);
  }

  if (priority && priority.size > 0) {
    unique.sort((a, b) => {
      const pa = priority.has(a) ? 0 : 1;
      const pb = priority.has(b) ? 0 : 1;
      return pa - pb;
    });
  }

  const capped = maxUids != null ? unique.slice(0, Math.max(0, maxUids)) : unique;
  // Mémoire seule : knownReady/IDB ne compte pas tant que promoteToMemory n’a pas tourné.
  return capped.filter((uid) => !isMessageBodyInMemory(accountId, folder, uid));
}

/**
 * Hydrate IndexedDB / mémoire depuis DD02 en batch (bodies-bootstrap.php).
 * Bonus client — jamais requis pour afficher la liste (DD02 suffit).
 */
export async function hydrateBodiesFromServerBootstrap(
  accountId: number,
  folder: string,
  uids: number[],
  options?: {
    /** UIDs à traiter en premier (non-lus, pending reveal…). */
    priorityUids?: ReadonlySet<number>;
    /** Ne pas dépasser N UID au total (défaut = tous). */
    maxUids?: number;
    timeoutMs?: number;
  }
): Promise<number> {
  const key = folderKey(accountId, folder);
  const pending = inflight.get(key);
  if (pending) {
    await pending;
  }

  const queue = buildQueue(
    accountId,
    folder,
    uids,
    options?.priorityUids,
    options?.maxUids
  );
  if (queue.length === 0) return 0;

  const timeoutMs = options?.timeoutMs ?? 25_000;

  const promise = (async (): Promise<number> => {
    let written = 0;
    const warmedFlags: Record<string, boolean> = {};

    for (let i = 0; i < queue.length; i += INBOX_BODIES_BOOTSTRAP_PAGE) {
      const page = queue.slice(i, i + INBOX_BODIES_BOOTSTRAP_PAGE);
      const need = page.filter((uid) => !isMessageBodyInMemory(accountId, folder, uid));
      if (need.length === 0) continue;

      const res = await fetchBodiesBootstrap(
        {
          accountId,
          folder,
          uids: need,
        },
        { timeoutMs }
      );
      if (!res.ok) break;

      for (const raw of res.data.messages ?? []) {
        const detail = sanitizeMailMessageDetail(raw);
        if (!isFetchedBodyComplete(detail) || detail.uid <= 0) continue;
        writeMessageBodyCache(accountId, folder, detail);
        warmedFlags[String(detail.uid)] = true;
        written += 1;
      }
    }

    if (Object.keys(warmedFlags).length > 0) {
      notifyBodiesWarmed(accountId, folder, warmedFlags);
    }

    return written;
  })();

  inflight.set(key, promise);
  try {
    return await promise;
  } finally {
    if (inflight.get(key) === promise) {
      inflight.delete(key);
    }
  }
}
